/* eslint-disable @typescript-eslint/no-explicit-any */
import { useCallback, useEffect, useState } from 'react';
import { Card, CardHeader, Field, Tooltip, Radio, RadioGroup, Skeleton, SkeletonItem, Spinner, Text, Link } from '@fluentui/react-components';
import { Info16Regular } from '@fluentui/react-icons';
import { useStyles } from './styles';
import { useAzureProvider } from 'src/azure/context';
import { useAutomationRuleFormProvider } from '../form';
import { updateAutomationRuleActorSettingsService } from 'src/services/appgami-api/update-automation-rule-actor-settings';
import { useAutomationRuleSettings } from './provider';
import { searchIdentities } from 'src/azure/services/get-board-users';
import { useMsEntraAuthorization } from 'src/hooks/ms-entra-authorization';

let debounceTimeout: NodeJS.Timeout | null = null;

const toolTipContent = `
	To run rules as the "Appgami Automation" service principal, you must first add it as a user to your Azure DevOps organization. To do that, navigate to “Organization settings > Users > Add users” and search for “Appgami Automation”. Once added, you can select this option to ensure automation rules continue running independently of your personal account.
`;

export default function AutomationRuleSettings() {
	const {applicationContext: { projectId, organizationName, userId }} = useAzureProvider();
	const classes = useStyles();                                                                                 

	const { goToPermissions, clearDebounceTimeout } = useMsEntraAuthorization();
	const {ruleId, actor: ruleActor, setActor: setRuleActor, createdBy} = useAutomationRuleFormProvider();
	const {
		spHasAccess,
		userHasAccess
	} = useAutomationRuleSettings();
	
	const automationRuleId = ruleId as string;

	const [state, setState] = useState({
		updating: false,
		isLoading: false,
		ownerName: '',
	});

	const changeState = useCallback((stateInput: Partial<typeof state>) => {
		setState(prevState => ({...prevState, ...stateInput}));
	}, []);


	const updateActor = useCallback(async (actor: string) => {
		changeState({ updating: true });

		if (!ruleId && actor === 'service-principal') {
			setRuleActor('service-principal');
			changeState({ updating: false });
		}

		if (!ruleId && actor === 'user') {
			setRuleActor('user');
			changeState({ updating: false });
		}

		if (ruleId) {
			if (debounceTimeout) {
				clearTimeout(debounceTimeout);
			}
	
			debounceTimeout = setTimeout(async () => {
				await updateAutomationRuleActorSettingsService({
					projectId: projectId,
					organizationName: organizationName,
					automationRuleId: automationRuleId,
					actor: actor as 'service-principal' | 'user',
				});
	
				if (actor === 'user') {
					setRuleActor('user');
					changeState({ updating: false });
				}
	
				if (actor === 'service-principal') {
					setRuleActor('service-principal');
					changeState({ updating: false });
				}
			}, 2000);
		}
	}, [organizationName, projectId, automationRuleId, ruleId]);

	const getOwnerDisplayName = useCallback(async (authorizedUserId: string): Promise<string> => {
		const identities = await searchIdentities(authorizedUserId, {queryTypeHint: 'uid'});
		const identity: any = identities.find((item: any) => item.localId === authorizedUserId);
		return identity.displayName;
	}, []);

	const init = useCallback(async () => {
		changeState({ isLoading: true });
		try {
			if (!ruleId) {
				changeState({
					ownerName: await getOwnerDisplayName(userId),
					isLoading: false,
				});
			}

			if (ruleId) {
				changeState({
					ownerName: await getOwnerDisplayName(createdBy),
					updating: false,
					isLoading: false,
				});
			}
		} catch (error) {
			console.error(error);
		}
	}, [organizationName, projectId, ruleId, ruleActor, userId, createdBy]);

	useEffect(() => {
		init();
		return () => {
			clearDebounceTimeout();
		};
	}, []);

	return (
		<div>
			<Card>
				<CardHeader
					header={
						<Text className={classes.title}>
              People
						</Text>
					}
				/>
				<span className={classes.ownerLabel}>Author</span>
				{state.isLoading && (
					<Skeleton className={classes.ownerSkeleton}>
						<SkeletonItem className={classes.ownerSkeletonItem} />
					</Skeleton>
				)}
				{!state.isLoading && (
					<span className={classes.ownerValue}>{state.ownerName}</span>
				)}
				<Field label="Actor">
					<RadioGroup
						value={ruleActor}
						onChange={(_, data) => {
							updateActor(data.value);
						}}
					>
						{!state.isLoading && (
							<>
								<span className={classes.actorOptionsRow}>
									<Radio
										value="user"
										label={`Author (${state.ownerName})`}
										disabled={state.updating || !userHasAccess}
									/>
									{ruleActor === 'service-principal' && state.updating && <Spinner size="tiny" />}
									{!userHasAccess && <Link onClick={goToPermissions}>authorize</Link>}
								</span>
								<span className={classes.actorOptionsRow}>
									<Radio
										value="service-principal"
										label="Appgami Automation"
										disabled={state.updating || !spHasAccess}
									/>
									<Tooltip content={toolTipContent} relationship="label">
										<Info16Regular />
									</Tooltip>
									{ruleActor === 'user' && state.updating && <Spinner size="tiny" />}
								</span>
							</>
						)}
					</RadioGroup>
					{state.isLoading && (
						<>
							<Skeleton className={classes.ownerSkeleton}>
								<SkeletonItem className={classes.ownerSkeletonItem} />
							</Skeleton>
							<Skeleton className={classes.ownerSkeleton}>
								<SkeletonItem className={classes.ownerSkeletonSP} />
							</Skeleton>
						</>
					)}
				</Field>
			</Card>
		</div>
	);
}
